import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import GarageInfoTab from '../components/profile/GarageInfoTab';
import BankTab from '../components/profile/BankTab';
import MediaTab from '../components/profile/MediaTab';
import ActivationTab from '../components/profile/ActivationTab';
import EditGaragePanel from '../components/profile/EditGaragePanel';
import PendingChangesBanner from '../components/profile/PendingChangesBanner';
import ViewChangesDialog from '../components/profile/ViewChangesDialog';
import { EditIcon, TrashIcon } from '../components/icons/SidebarIcons';
import '../styles/profile.css';

type TabKey = 'info' | 'bank' | 'media' | 'activation';

interface GarageProfile {
  id: string;
  name: string;
  status: string;
  blockedAt: string | null;
  managerName: string;
  phone: string;
  email: string;
  dailyCapacity: number;
  numberOfWorkers: number;
  licenseNumber: string;
  city: string;
  area: string;
  cityId: string | null;
  areaId: string | null;
  streetAddress: string;
  latitude: number | null;
  longitude: number | null;
  logoStorageKey: string | null;
  accountHolder: string | null;
  bankName: string | null;
  iban: string | null;
  /** Spec 081a (present once the backend returns it on GET /api/garages/me). */
  description?: string | null;
  descriptionAr?: string | null;
  operatingHours: Array<{
    dayOfWeek: number;
    openTime: string | null;
    closeTime: string | null;
    isClosed: boolean;
  }>;
  media: Array<{
    id: string;
    storageKey: string;
    mediaType: string;
    sortOrder: number;
  }>;
}

interface StagedChanges {
  garageInfo?: Record<string, unknown>;
  bank?: Record<string, unknown>;
  media?: Record<string, unknown>;
}

export default function GarageProfilePage() {
  const { t } = useLanguage();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('info');
  const [garage, setGarage] = useState<GarageProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<Record<string, unknown> | null>(null);
  const [lastRejected, setLastRejected] = useState<{ rejectionReason: string; reviewedAt: string } | null>(null);
  const [editDialog, setEditDialog] = useState<TabKey | null>(null);
  const [stagedChanges, setStagedChanges] = useState<StagedChanges>({});
  const [submitting, setSubmitting] = useState(false);

  const hasStagedChanges = Object.keys(stagedChanges).length > 0;
  const hasPending = pendingRequest !== null;

  useEffect(() => {
    if (!auth.token) return;
    loadProfile();
    loadPendingRequest();
    loadLastRejected();
  }, [auth.token]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await api.getMyGarage(auth.token!);
      setGarage(data as unknown as GarageProfile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequest = async () => {
    try {
      const pending = await api.get<Record<string, unknown>>('/api/garages/me/change-requests/pending', auth.token!);
      setPendingRequest(pending && pending.id ? pending : null);
    } catch {
      setPendingRequest(null);
    }
  };

  // Show the rejection banner only when the most recent change request was rejected.
  // If the latest request is APPROVED or PENDING, the rejection is stale and stays hidden.
  const loadLastRejected = async () => {
    try {
      const list = await api.get<Array<{ status: string; rejectionReason: string | null; reviewedAt: string | null }>>(
        '/api/garages/me/change-requests',
        auth.token!,
      );
      const latest = Array.isArray(list) && list.length > 0 ? list[0] : null;
      if (latest && latest.status === 'REJECTED' && latest.rejectionReason && latest.reviewedAt) {
        setLastRejected({ rejectionReason: latest.rejectionReason, reviewedAt: latest.reviewedAt });
      } else {
        setLastRejected(null);
      }
    } catch {
      setLastRejected(null);
    }
  };

  const handleSubmitChanges = async () => {
    if (!hasStagedChanges) return;
    setSubmitting(true);
    try {
      await api.submitChangeRequest(stagedChanges, auth.token!);
      setStagedChanges({});
      await loadPendingRequest();
      await loadLastRejected();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit changes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    try {
      await api.delete('/api/garages/me/change-requests/pending', auth.token!);
      setPendingRequest(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel request');
    }
  };

  const [showChanges, setShowChanges] = useState(false);

  const handleUpdateEmail = async (newEmail: string) => {
    try {
      await api.patch('/api/garages/me/email', { email: newEmail }, auth.token!);
      await loadProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update email');
    }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'info', label: t('profile.tab.garageInfo') },
    { key: 'bank', label: t('profile.tab.bank') },
    { key: 'media', label: t('profile.tab.media') },
    { key: 'activation', label: t('profile.tab.activation') },
  ];

  const hasPendingBankChange = (() => {
    if (!pendingRequest) return false;
    const proposed = (pendingRequest.proposedChanges as Record<string, unknown> | undefined)?.garageInfo as Record<string, unknown> | undefined;
    if (!proposed) return false;
    return 'accountHolder' in proposed || 'bankName' in proposed || 'iban' in proposed;
  })();

  if (loading) return <div className="profile-loading">{t('common.loading')}</div>;
  if (error || !garage) return <div className="profile-error">{error || 'Failed to load profile'}</div>;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-header-left">
          <h1 className="profile-title">
            {t('profile.title')}{' '}
            <span className="profile-subtitle">{t('profile.subtitle')}</span>
          </h1>
          <h2 className="profile-garage-name">{garage.name}</h2>
        </div>
        <div className="profile-header-right">
          {hasPending && (
            <span className="profile-status-badge pending">{t('profile.status.pending')}</span>
          )}
          {!hasPending && activeTab !== 'activation' && (
            <>
              <button className="profile-trash-btn" title={t('common.delete')}>
                <TrashIcon size={24} />
              </button>
              <button
                className="profile-edit-action-btn"
                onClick={() => setEditDialog(activeTab)}
              >
                <EditIcon size={18} />
                <span>{t('common.edit')}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <PendingChangesBanner
        pendingRequest={pendingRequest as { status: string; createdAt: string; proposedChanges?: Record<string, unknown> } | null}
        lastRejected={lastRejected}
        onCancel={hasPending ? handleCancelRequest : undefined}
        onViewChanges={hasPending ? () => setShowChanges(!showChanges) : undefined}
        currentGarage={garage}
      />

      {showChanges && pendingRequest && 'proposedChanges' in pendingRequest && (
        <ViewChangesDialog
          proposedChanges={pendingRequest.proposedChanges as Record<string, unknown>}
          currentGarage={garage}
          token={auth.token!}
          onClose={() => setShowChanges(false)}
        />
      )}

      {hasStagedChanges && (
        <div className="staged-changes-bar">
          <span>{t('profile.staged.message')}
            {stagedChanges.garageInfo && ` • ${t('profile.tab.garageInfo')}`}
            {stagedChanges.garageInfo && garage
              && (((stagedChanges.garageInfo.description as string | undefined) ?? '') !== (garage.description ?? '')
                || ((stagedChanges.garageInfo.descriptionAr as string | undefined) ?? '') !== (garage.descriptionAr ?? ''))
              && ` (${t('profile.info.description') !== 'profile.info.description' ? t('profile.info.description') : 'Description'})`}
            {stagedChanges.media && ` • ${t('profile.tab.media')}`}
          </span>
          <div className="staged-changes-actions">
            <button className="dialog-btn-secondary" onClick={() => setStagedChanges({})}>{t('common.discard')}</button>
            <button className="dialog-btn-primary" onClick={handleSubmitChanges} disabled={submitting}>
              {submitting ? t('common.submitting') : t('profile.edit.submitChanges')}
            </button>
          </div>
        </div>
      )}

      <div className="profile-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`profile-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {((tab.key === 'info' && stagedChanges.garageInfo) ||
              (tab.key === 'bank' && stagedChanges.bank) ||
              (tab.key === 'media' && stagedChanges.media)) && (
              <span className="tab-change-dot" />
            )}
          </button>
        ))}
      </div>

      {/* Show edit panel OR read-only content */}
      {editDialog ? (
        <EditGaragePanel
          garage={garage as unknown as Parameters<typeof EditGaragePanel>[0]['garage']}
          initialTab={editDialog}
          token={auth.token!}
          onSave={(changes) => {
            setStagedChanges(changes as StagedChanges);
            setEditDialog(null);
          }}
          onCancel={() => setEditDialog(null)}
        />
      ) : (
        <div className="profile-content">
          {activeTab === 'info' && (
            <GarageInfoTab
              garage={garage}
              onUpdateEmail={handleUpdateEmail}
            />
          )}
          {activeTab === 'bank' && (
            <BankTab
              garage={garage}
              pendingBankChange={hasPendingBankChange}
            />
          )}
          {activeTab === 'media' && <MediaTab garage={garage} token={auth.token!} />}
          {activeTab === 'activation' && <ActivationTab currentStatus={garage.status} blockedAt={garage.blockedAt} token={auth.token!} onStatusChange={loadProfile} />}
        </div>
      )}
    </div>
  );
}
